import React, { useEffect, useState } from "react";
import { FaChevronDown, FaPlus, FaTimes, FaAsterisk } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import Switch from "react-switch";
import { useUpdateUserProfile } from "../hooks/updateUserProfile";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

const EditEmergencyContact = ({ userProfile, isLoading, refetch }) => {
  const {
    updateProfile,
    isLoading: isUpdating,
    error,
  } = useUpdateUserProfile();

  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [showContactDetails, setShowContactDetails] = useState({});

  // Initialize emergency contacts from userProfile
  useEffect(() => {
    if (
      userProfile?.emergencyContacts &&
      Array.isArray(userProfile.emergencyContacts)
    ) {
      setEmergencyContacts(userProfile.emergencyContacts);
      // Initialize show details state
      const showState = {};
      userProfile.emergencyContacts.forEach((_, index) => {
        showState[index] = false;
      });
      setShowContactDetails(showState);
    } else if (userProfile) {
      // If no emergency contacts exist, create an empty array
      setEmergencyContacts([]);
      setShowContactDetails({});
    }
  }, [userProfile]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 11) {
      return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(
        4,
        7
      )}-${cleaned.slice(7, 11)}`;
    } else if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
        10
      )}`;
    } else if (cleaned.length >= 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const validateContact = (contact) => {
    return (
      contact.firstName &&
      contact.lastName &&
      contact.relationship &&
      (contact.homePhoneNumber ||
        contact.workPhoneNumber ||
        contact.mobilePhoneNumber)
    );
  };

  const handleContactChange = (index, field, value) => {
    setEmergencyContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    );
  };

  const handlePhoneChange = (index, phoneType, value) => {
    const formatted = formatPhoneNumber(value);
    handleContactChange(index, phoneType, formatted);
  };

  const handlePreferredContactChange = (index) => {
    setEmergencyContacts((prev) =>
      prev.map((contact, i) => ({
        ...contact,
        preferred: i === index,
      }))
    );
  };

  const handlePreferredPhoneChange = (index, phoneType) => {
    setEmergencyContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, preferredPhone: phoneType } : contact
      )
    );
  };

  const toggleContactDetails = (index) => {
    setShowContactDetails((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const removeContact = (index) => {
    if (emergencyContacts.length <= 1) {
      toast.error("At least one emergency contact is required");
      return;
    }

    setEmergencyContacts((prev) => prev.filter((_, i) => i !== index));
    setShowContactDetails((prev) => {
      const newState = { ...prev };
      delete newState[index];
      // Reindex remaining contacts
      const reindexed = {};
      Object.keys(newState).forEach((key) => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexed[oldIndex - 1] = newState[key];
        } else {
          reindexed[oldIndex] = newState[key];
        }
      });
      return reindexed;
    });
  };

  const addNewContact = () => {
    const newContact = {
      firstName: "",
      lastName: "",
      relationship: "",
      address: "",
      suite: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      homePhone: "",
      homePhoneExt: "",
      workPhone: "",
      workPhoneExt: "",
      mobilePhone: "",
      mobilePhoneExt: "",
      preferredPhone: "mobile",
      preferred: emergencyContacts.length === 0,
    };

    setEmergencyContacts((prev) => [...prev, newContact]);
    setShowContactDetails((prev) => ({
      ...prev,
      [emergencyContacts.length]: true,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emergencyContacts.length === 0) {
      toast.error("At least one emergency contact is required");
      return;
    }

    // Validate all contacts
    const invalidContacts = emergencyContacts.filter(
      (contact, index) => !validateContact(contact)
    );
    if (invalidContacts.length > 0) {
      toast.error(
        "Please ensure all contacts have first name, last name, relationship, and at least one phone number"
      );
      return;
    }

    try {

      const updatedData = {
        id: userProfile.id,
        emergencyContacts: emergencyContacts,
      };

      await updateProfile(updatedData);

      const isFirstLogin = window.location.pathname.includes("first_login_profile");
      if (!isFirstLogin) {
        toast.success("Emergency contacts updated successfully!");
      }

      // Close modal
      const modal = document.getElementById("editEmergencyContact");
      if (modal) {
        window.$(modal).modal("hide");
        refetch && refetch();
      }
    } catch (error) {
      console.error("Failed to update emergency contacts:", error);
      toast.error("Failed to update emergency contacts. Please try again.");
    }
  };

  return (
    <div
      className="modal fade"
      id="editEmergencyContact"
      tabIndex="-1"
      aria-labelledby="editEmergencyContact"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <form onSubmit={handleSubmit}>
          <div className="modal-content border-0 overlay-rounded">
            <div className="modal-header mt-14 border-0 pb-2 mb-1">
              <label className="fs-15" id="editEmergencyContact">
                EDIT: Emergency Contact Information
              </label>

              <FaTimes
                size={15}
                className="cursor-pointer text_primary"
                data-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body py-0">
              <div className="d-flex justify-content-end">
                <p className="text_primary small-text">
                  Select Preferred Emergency Contact:
                </p>
              </div>

              {/* Existing Emergency Contacts */}
              {emergencyContacts.map((contact, index) => (
                <div key={index}>
                  <div className="d-flex justify-content-between p-0 mb-3">
                    <div className="d-flex small-text justify-content-between">
                      <label className="font-weight-bold">
                        {emergencyContacts.length > 1 && (
                          <MdClose
                            className="text_danger cursor-pointer mr-4"
                            size={20}
                            onClick={() => removeContact(index)}
                          />
                        )}
                        {index === 0 ? "Primary" : "Additional"} Emergency
                        Contact:
                      </label>
                      <FaChevronDown
                        size={20}
                        className="text_primary ml-54 cursor-pointer"
                        style={{
                          transform: showContactDetails[index]
                            ? "rotate(180deg)"
                            : "rotate(0)",
                          transition: "all 0.3s linear",
                        }}
                        onClick={() => toggleContactDetails(index)}
                      />
                    </div>
                    <div className="d-flex small-text">
                      {contact.preferred && (
                        <div className="badge_pomme bg_primary f-10 text-light rounded-pill d-flex align-items-center justify-content-center">
                          <span>Preferred Emergency Contact</span>
                        </div>
                      )}
                      <div className="ml-4">
                        <Switch
                          onChange={() => handlePreferredContactChange(index)}
                          checked={contact.preferred || false}
                          onColor="#5D8FA8"
                          onHandleColor="#50a193"
                          handleDiameter={20}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                          height={14}
                          width={35}
                          disabled={isUpdating}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Details Form */}
                  <div
                    className={`slide-down small-text ml-5 ${
                      showContactDetails[index] ? "show" : ""
                    }`}
                  >
                    {/* Basic Information */}
                    <div className="form-group row mb-12">
                      <label className="col-md-3 col-form-label">
                        First Name:
                      </label>
                      <div className="col-md-7">
                        <input
                          type="text"
                          className="small-text overlay-phone-input"
                          value={contact.firstName || ""}
                          onChange={(e) =>
                            handleContactChange(
                              index,
                              "firstName",
                              e.target.value
                            )
                          }
                          disabled={isUpdating}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-12">
                      <label className="col-md-3 col-form-label">
                        Last Name:
                      </label>
                      <div className="col-md-7">
                        <input
                          type="text"
                          className="small-text overlay-phone-input"
                          value={contact.lastName || ""}
                          onChange={(e) =>
                            handleContactChange(
                              index,
                              "lastName",
                              e.target.value
                            )
                          }
                          disabled={isUpdating}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group row mb-12">
                      <label className="col-md-3 col-form-label">
                        Relationship:
                      </label>
                      <div className="col-md-7">
                        <input
                          type="text"
                          className="small-text overlay-phone-input"
                          value={contact.relationship || ""}
                          onChange={(e) =>
                            handleContactChange(
                              index,
                              "relationship",
                              e.target.value
                            )
                          }
                          placeholder="e.g., Spouse, Parent, Sibling"
                          disabled={isUpdating}
                          required
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="form-group row mb-1">
                      <label className="col-md-3 col-form-label pr-0">
                        Home Address:
                      </label>
                      <div className="col-md-7 d-flex flex-column">
                        <input
                          type="text"
                          className="small-text overlay-phone-input mb-12"
                          placeholder="Street Address"
                          value={contact.streetAddress || ""}
                          onChange={(e) =>
                            handleContactChange(
                              index,
                              "streetAddress",
                              e.target.value
                            )
                          }
                          disabled={isUpdating}
                        />
                        <input
                          type="text"
                          className="small-text overlay-phone-input mb-12"
                          placeholder="Suite/Apt No."
                          value={contact.suite || ""}
                          onChange={(e) =>
                            handleContactChange(index, "suite", e.target.value)
                          }
                          disabled={isUpdating}
                        />
                        <div className="form-group row mb-0">
                          <div className="col-md-5 mb-12">
                            <input
                              type="text"
                              className="small-text overlay-phone-input"
                              placeholder="City"
                              value={contact.city || ""}
                              onChange={(e) =>
                                handleContactChange(
                                  index,
                                  "city",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                            />
                          </div>
                          <div className="col-md-7 pl-2">
                            <input
                              type="text"
                              className="small-text overlay-phone-input"
                              placeholder="Country / Region"
                              value={contact.country || ""}
                              onChange={(e) =>
                                handleContactChange(
                                  index,
                                  "country",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                            />
                          </div>
                        </div>
                        <div className="form-group row">
                          <div className="col-md-5 mb-1">
                            <input
                              type="text"
                              className="small-text overlay-phone-input"
                              placeholder="Province / State"
                              value={contact.province || ""}
                              onChange={(e) =>
                                handleContactChange(
                                  index,
                                  "province",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                            />
                          </div>
                          <div className="col-md-5 pl-2">
                            <input
                              type="text"
                              className="small-text overlay-phone-input"
                              placeholder="Postal Code / Zip"
                              value={contact.postalCode || ""}
                              onChange={(e) =>
                                handleContactChange(
                                  index,
                                  "postalCode",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Phone Numbers */}
                    {["home", "work", "mobile"].map((phoneType) => (
                      <div key={phoneType} className="form-group row mb-1">
                        <label className="col-md-3 col-form-label pr-0">
                          {phoneType.charAt(0).toUpperCase() +
                            phoneType.slice(1)}{" "}
                          Phone Number:
                        </label>
                        <div className="col-md-7">
                          <div className="form-group row align-items-center mb-2">
                            <div className="col-md-5">
                              <input
                                type="text"
                                className="small-text overlay-phone-input"
                                placeholder="1-234-567-8900"
                                value={contact[`${phoneType}PhoneNumber`] || ""}
                                onChange={(e) =>
                                  handlePhoneChange(
                                    index,
                                    `${phoneType}PhoneNumber`,
                                    e.target.value
                                  )
                                }
                                disabled={isUpdating}
                              />
                            </div>
                            <div className="col-md-4 pl-2 d-flex align-items-center">
                              <input
                                type="text"
                                className="small-text overlay-extension-input"
                                placeholder="Extension"
                                value={contact[`${phoneType}PhoneExt`] || ""}
                                onChange={(e) =>
                                  handleContactChange(
                                    index,
                                    `${phoneType}PhoneExt`,
                                    e.target.value
                                  )
                                }
                                disabled={isUpdating}
                              />
                              {phoneType === "mobile" && (
                                <FaAsterisk
                                  className="text_primary ml-2"
                                  size={12}
                                />
                              )}
                            </div>
                            <Switch
                              onChange={() =>
                                handlePreferredPhoneChange(index, phoneType)
                              }
                              checked={contact.preferredPhone === phoneType}
                              onColor="#5D8FA8"
                              onHandleColor="#50a193"
                              handleDiameter={20}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                              height={14}
                              width={35}
                              disabled={isUpdating}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Add Another Contact Button */}
              <div className="mt-4">
                <button
                  type="button"
                  className="btn btn-link text_primary f-11 text-decoration-none"
                  onClick={addNewContact}
                >
                  <FaPlus size={10} className="mr-1" /> Add another Emergency
                  Contact
                </button>
              </div>
            </div>

            <div className="modal-footer border-0 d-flex justify-content-between mt-5">
              <button
                type="button"
                className="btn btn_outline_danger rounded overlay-btn"
                data-dismiss="modal"
              >
                Close
              </button>
              <button
                type="submit"
                className="btn btn-outline-primary rounded overlay-btn"
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmergencyContact;
